// Hook for offline detection and management
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '../contexts/AuthContext';
import * as offlineDB from '../utils/offlineDB';

// Intervalo de sincronización automática mientras hay conexión (cada 5 min por defecto)
export const SYNC_INTERVAL_MS = 5 * 60 * 1000;

// Reenvío por defecto de acciones encoladas al backend
const defaultApiCall = async (action) => {
  if (action.type !== 'CREATE_ORDER') {
    // Tipos desconocidos no son reenciables: se descartan para no atascar la cola
    console.warn('[useOffline] Acción sin handler, descartando:', action.type);
    return 'skipped';
  }
  const payload = action.payload || {};
  const token = localStorage.getItem('token');
  await axios.post(
    `${API}/orders`,
    {
      items: payload.items,
      order_type: payload.order_type,
      customer_name: payload.customer_name,
      customer_phone: payload.customer_phone,
      table_id: payload.table_id,
      notes: payload.notes
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return 'sent';
};

export const useOffline = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingActions, setPendingActions] = useState(0);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  // Initialize offline database
  useEffect(() => {
    const initOffline = async () => {
      try {
        await offlineDB.initDB();
        setIsInitialized(true);
        console.info('[useOffline] Offline DB initialized');
        
        // Load pending actions count
        const queue = await offlineDB.getOfflineQueue();
        setPendingActions(queue.length);
      } catch (error) {
        console.error('[useOffline] Failed to initialize offline DB:', error);
      }
    };

    initOffline();
  }, []);

  // Online/offline event listeners
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Conexión restaurada', {
        description: 'Sincronizando datos...',
        icon: '🌐'
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Sin conexión', {
        description: 'Modo offline activado',
        icon: '📴'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Listen for sync messages from service worker
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'SYNC_OFFLINE_ACTIONS') {
        syncOfflineActions();
      }
    };

    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, []);

  // Add action to offline queue
  const addOfflineAction = useCallback(async (action) => {
    try {
      await offlineDB.addToOfflineQueue(action);
      const queue = await offlineDB.getOfflineQueue();
      setPendingActions(queue.length);
      
      toast.info('Acción guardada', {
        description: 'Se sincronizará cuando haya conexión',
        icon: '💾'
      });
      
      return true;
    } catch (error) {
      console.error('[useOffline] Failed to queue action:', error);
      return false;
    }
  }, []);

  // Sync offline actions with server
  const syncOfflineActions = useCallback(async (apiCall) => {
    if (!isOnline) return { synced: 0, failed: 0 };

    try {
      const queue = await offlineDB.getOfflineQueue();
      if (queue.length === 0) return { synced: 0, failed: 0 };

      console.info(`[useOffline] Syncing ${queue.length} offline actions...`);
      
      // Sin apiCall explícito se usa el reenvío real por defecto:
      // NUNCA se vacía la cola sin haber sincronizado (antes se perdían acciones)
      const call = apiCall || defaultApiCall;

      let synced = 0;
      let failed = 0;

      for (const action of queue) {
        try {
          const outcome = await call(action);
          if (outcome === 'skipped') {
            await offlineDB.removeFromOfflineQueue(action.id);
            continue;
          }
          // Confirmar envío: marcar el pedido local como sincronizado para no duplicarlo
          if (action.type === 'CREATE_ORDER' && action.offlineOrderId) {
            await offlineDB.markOrderSynced(action.offlineOrderId);
          }
          await offlineDB.removeFromOfflineQueue(action.id);
          synced++;
        } catch (error) {
          console.error('[useOffline] Failed to sync action:', action, error);
          failed++;
        }
      }

      const remainingQueue = await offlineDB.getOfflineQueue();
      setPendingActions(remainingQueue.length);

      if (synced > 0 || failed === 0) {
        setLastSyncAt(new Date().toISOString());
      }

      if (synced > 0) {
        toast.success(`${synced} acción(es) sincronizada(s)`, {
          icon: '✅'
        });
      }

      return { synced, failed };
    } catch (error) {
      console.error('[useOffline] Sync error:', error);
      return { synced: 0, failed: 0 };
    }
  }, [isOnline]);

  // Sincronización periódica cada X minutos mientras hay conexión y cola pendiente
  const syncStateRef = useRef({ isOnline, pendingActions });
  const syncActionsRef = useRef(null);
  useEffect(() => {
    syncStateRef.current = { isOnline, pendingActions };
    syncActionsRef.current = syncOfflineActions;
  }, [isOnline, pendingActions, syncOfflineActions]);
  useEffect(() => {
    const timer = setInterval(() => {
      const { isOnline: online, pendingActions: pending } = syncStateRef.current;
      if (online && pending > 0 && syncActionsRef.current) {
        syncActionsRef.current();
      }
    }, SYNC_INTERVAL_MS);
    return () => clearInterval(timer);
  }, []);

  // Cache data for offline use
  const cacheData = useCallback(async (type, data) => {
    try {
      switch (type) {
        case 'products':
          await offlineDB.saveProducts(data);
          // Also cache product images
          await offlineDB.cacheProductImages(data);
          break;
        case 'categories':
          await offlineDB.saveCategories(data);
          break;
        case 'tables':
          await offlineDB.saveTables(data);
          break;
        case 'ingredients':
          // Cache ingredient images
          await offlineDB.cacheIngredientImages(data);
          break;
        default:
          console.warn('[useOffline] Unknown cache type:', type);
      }
    } catch (error) {
      console.error('[useOffline] Failed to cache data:', error);
    }
  }, []);

  // Get cached data
  const getCachedData = useCallback(async (type) => {
    try {
      switch (type) {
        case 'products':
          // Return products with cached images
          return await offlineDB.getProductsWithCachedImages();
        case 'categories':
          return await offlineDB.getCategories();
        case 'tables':
          return await offlineDB.getTables();
        default:
          return [];
      }
    } catch (error) {
      console.error('[useOffline] Failed to get cached data:', error);
      return [];
    }
  }, []);

  // Cache a single image
  const cacheImage = useCallback(async (url, type = 'product') => {
    try {
      return await offlineDB.cacheImage(url, type);
    } catch (error) {
      console.error('[useOffline] Failed to cache image:', error);
      return url;
    }
  }, []);

  // Get cached image
  const getCachedImage = useCallback(async (url) => {
    try {
      return await offlineDB.getCachedImage(url);
    } catch (error) {
      console.error('[useOffline] Failed to get cached image:', error);
      return null;
    }
  }, []);

  // Get image stats
  const getImageCacheStats = useCallback(async () => {
    try {
      return await offlineDB.getCachedImagesStats();
    } catch (_error) {
      return { count: 0, totalSizeBytes: 0, totalSizeMB: '0' };
    }
  }, []);

  // Create offline order
  const createOfflineOrder = useCallback(async (orderData) => {
    try {
      const offlineOrder = await offlineDB.saveOfflineOrder(orderData);
      
      // Also add to sync queue
      await addOfflineAction({
        type: 'CREATE_ORDER',
        payload: orderData,
        offlineOrderId: offlineOrder.id
      });
      
      return offlineOrder;
    } catch (error) {
      console.error('[useOffline] Failed to create offline order:', error);
      throw error;
    }
  }, [addOfflineAction]);

  // Get pending offline orders
  const getOfflineOrders = useCallback(async () => {
    try {
      return await offlineDB.getOfflineOrders();
    } catch (error) {
      console.error('[useOffline] Failed to get offline orders:', error);
      return [];
    }
  }, []);

  return {
    isOnline,
    isOffline: !isOnline,
    isInitialized,
    pendingActions,
    lastSyncAt,
    addOfflineAction,
    syncOfflineActions,
    cacheData,
    getCachedData,
    createOfflineOrder,
    getOfflineOrders,
    cacheImage,
    getCachedImage,
    getImageCacheStats
  };
};

export default useOffline;
